import { useEffect, useMemo, useRef, useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import api from '@/lib/api';
import { toast } from 'sonner';
import { BellRing } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import BigTooltip from '@/components/team-config/BigTooltip';
import FieldTooltip from '@/components/ui/FieldTooltip';

const leadEvents = [
  { key: 'new_lead_to_lead', title: 'Nuevo lead registrado' },
  { key: 'no_response_24h', title: 'Lead sin respuesta 24h' },
  { key: 'followup_day_3', title: 'Seguimiento día 3' },
  { key: 'lead_reactivated', title: 'Lead reactivado' },
];

const vendorEvents = [
  { key: 'new_lead_to_vendor', title: 'Nuevo lead asignado' },
  { key: 'lead_urgent', title: 'Lead urgente' },
  { key: 'no_attend_1h', title: 'Lead sin atender 1 hora' },
  { key: 'reassign_from_vendor', title: 'Cuando le quitas un lead a un vendedor' },
  { key: 'reassign_to_vendor', title: 'Cuando le das un lead a un vendedor' },
];

const EVENT_HELP = {
  new_lead_to_lead: {
    title: 'Aviso al cliente nuevo',
    body: 'WhatsApp que recibe la persona que acaba de dejar sus datos (María). Solo se manda si el mensaje ya está aprobado.',
    useCase: 'María llena la ficha de GMCCUAUTLA y le llega: “Hola María, recibimos tu interés…”',
  },
  no_response_24h: {
    title: 'Si María no contestó',
    body: 'Un recordatorio al día siguiente. WhatsApp no deja mandar un texto inventado: tiene que ser este mensaje aprobado.',
    useCase: 'Pasaron 24 horas y María no respondió. Le llega un “¿sigues interesada?”',
  },
  followup_day_3: {
    title: 'Seguimiento del día 3',
    body: 'Tercer toque automático. Úsalo para invitar a una visita, no para vender.',
    useCase: 'El miércoles María recibe: “¿Agendamos tu visita a GMCCUAUTLA?”',
  },
  lead_reactivated: {
    title: 'María volvió',
    body: 'Se manda cuando un lead frío se mueve otra vez. El vendedor no tiene que enterarse por WhatsApp aquí: esto va al lead.',
    useCase: 'María reabre el anuncio. Le llega “vimos que volviste a consultar…”',
  },
  new_lead_to_vendor: {
    title: 'Aviso al vendedor',
    body: 'Le avisa a Ana (o a quien toque) que tiene un lead nuevo. No es lo mismo que reasignar: esto es la primera vez.',
    useCase: 'Entra María. Ana recibe: “Tienes un lead nuevo: María López…”',
  },
  lead_urgent: {
    title: 'Lead urgente',
    body: 'Solo si alguien marca el lead como urgente. Si no usas esa marca, déjalo apagado.',
    useCase: 'Un gerente marca urgente y Ana recibe el aviso en el celular.',
  },
  no_attend_1h: {
    title: 'Nadie lo tocó en 1 hora',
    body: 'Empuja al vendedor si el lead lleva una hora sin atender. Apágalo si suena demasiado.',
    useCase: 'María llegó a las 10:00 y a las 11:00 Ana aún no la abrió.',
  },
  reassign_from_vendor: {
    title: 'Al que le quitas el lead',
    body: 'Cuando pasas a María de Ana a Carlos, Ana recibe este WhatsApp y deja de ver a María en su lista.',
    useCase: 'Luis reasigna. Ana lee: “Luis reasignó el lead María… ya no podrás interactuar.”',
  },
  reassign_to_vendor: {
    title: 'Al que recibe el lead',
    body: 'Carlos se entera en el momento, no cuando abra el CRM. Enciéndelo después de que WhatsApp apruebe el texto.',
    useCase: 'Carlos recibe: “Se te asignó un lead: María López. Te lo reasignó Luis.”',
  },
};

const statusPlain = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED') return { label: 'Lista para usar', tone: 'ok' };
  if (s === 'PENDING' || s === 'SUBMITTED') return { label: 'WhatsApp la está revisando', tone: 'wait' };
  if (s === 'REJECTED') return { label: 'WhatsApp la rechazó', tone: 'bad' };
  if (s === 'DEACTIVATED' || s === 'DISABLED') return { label: 'WhatsApp la apagó', tone: 'bad' };
  return { label: '', tone: 'none' };
};

const toneClass = {
  ok: 'text-emerald-300',
  wait: 'text-amber-300',
  bad: 'text-rose-300',
  none: 'text-zinc-400',
};

// Casillas de nuevo-lead que se configuran POR LÍNEA (sub-doc waba_accounts.notifications).
// Mapea la key de UI/notification_config → la key del sub-doc. Las demás casillas
// siguen 100% en notification_config.
const PER_LINE_EVENT_KEYS = {
  new_lead_to_lead: 'new_lead_lead',
  new_lead_to_vendor: 'new_lead_vendor',
};

const sourceOptions = [
  { value: 'lead_name', label: 'Nombre del lead', example: 'María López' },
  { value: 'lead_property', label: 'Lo que le interesa', example: 'Depto Torre A' },
  { value: 'lead_vendor', label: 'Vendedor asignado', example: 'Ana Ruiz' },
  { value: 'lead_phone', label: 'Teléfono del lead', example: '5215512345678' },
  { value: 'lead_email', label: 'Email del lead', example: 'maria@email.com' },
  { value: 'lead_id', label: 'ID interno del lead', example: 'lead-uuid' },
  { value: 'company_name', label: 'Nombre de la empresa', example: APP_NAME },
  { value: 'actor_name', label: 'Quién hizo el cambio', example: 'Luis Pérez' },
  { value: 'previous_vendor_name', label: 'Vendedor anterior', example: 'Ana Ruiz' },
  { value: 'new_vendor_name', label: 'Vendedor nuevo', example: 'Carlos Díaz' },
  { value: 'custom', label: 'Un texto que no cambia', example: 'Texto personalizado' },
];

const extractIndexes = (text) => {
  const out = new Set();
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  let m = regex.exec(String(text || ''));
  while (m) {
    out.add(Number(m[1]));
    m = regex.exec(String(text || ''));
  }
  return [...out].sort((a, b) => a - b);
};

const defaultSource = (idx) => {
  if (idx === 1) return 'lead_name';
  if (idx === 2) return 'lead_property';
  if (idx === 3) return 'lead_vendor';
  return 'custom';
};

const defaultEventConfig = {
  enabled: false,
  provider: 'gupshup',
  waba_config_id: '',
  waba_id: '',
  template_id: '',
  variable_mapping: [],
  twilio_content_sid: '',
  twilio_content_variables: {},
  message_text: '',
};

const buildTwilioVariableSchema = (mapping = []) => {
  const out = {};
  (Array.isArray(mapping) ? mapping : []).forEach((row) => {
    const idx = Number(row?.index);
    if (!Number.isFinite(idx) || idx <= 0) return;
    const source = String(row?.source || '').trim();
    if (!source) return;
    out[String(idx)] = source;
  });
  return out;
};

export const NotificacionesTab = ({ initialWabaConfigId = null, wabaAccountId = null } = {}) => {
  const [events, setEvents] = useState({});
  const [numbers, setNumbers] = useState([]);
  const [wabaDetail, setWabaDetail] = useState(null);
  const [templatesByWaba, setTemplatesByWaba] = useState({});
  const [twilioTemplates, setTwilioTemplates] = useState([]);
  const [waMode, setWaMode] = useState('gupshup');
  const [wabaProvider, setWabaProvider] = useState('gupshup');

  const [activeEventKey, setActiveEventKey] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submittingReassign, setSubmittingReassign] = useState(false);
  const [partnerReady, setPartnerReady] = useState(null);
  const [reassignSpecs, setReassignSpecs] = useState([]);
  const [submitNote, setSubmitNote] = useState('');
  const textareaRef = useRef(null);

  // Variables disponibles para insertar en el mensaje QR (Evolution).
  const QR_VARS = [
    { key: '{lead_name}', label: '👤 Nombre' },
    { key: '{lead_phone}', label: '📞 Teléfono' },
    { key: '{lead_vendor}', label: '🧑‍💼 Vendedor' },
    { key: '{lead_property}', label: '🏠 Propiedad' },
    { key: '{lead_email}', label: '✉️ Email' },
    { key: '{lead_id}', label: '🔑 ID Lead' },
    { key: '{company_name}', label: '🏢 Empresa' },
    { key: '{actor_name}', label: '✍️ Quién reasignó' },
    { key: '{previous_vendor_name}', label: '👤 Vendedor anterior' },
    { key: '{new_vendor_name}', label: '👤 Vendedor nuevo' },
  ];

  const insertQrVariable = (variable) => {
    const el = textareaRef.current;
    const val = String(activeEvent?.message_text || '');
    if (!el) {
      updateActiveEvent({ message_text: val + variable });
      return;
    }
    const start = el.selectionStart ?? val.length;
    const end = el.selectionEnd ?? val.length;
    const newVal = val.slice(0, start) + variable + val.slice(end);
    updateActiveEvent({ message_text: newVal });
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const loadAll = async () => {
    try {
      const detailReq = initialWabaConfigId
        ? api.get(`/whatsapp/waba/${wabaAccountId}/detail`).catch(() => ({ data: null }))
        : Promise.resolve({ data: null });
      const perLineReq = (initialWabaConfigId && wabaAccountId)
        ? api.get(`/waba-accounts/${wabaAccountId}/notifications`).catch(() => ({ data: null }))
        : Promise.resolve({ data: null });
      const [cfg, nums, twilioTpls, status, detail, perLineRes, partnerRes, specsRes] = await Promise.all([
        api.get('/whatsapp/notification-config'),
        api.get('/whatsapp/gupshup/numbers').catch(() => ({ data: { numbers: [] } })),
        api.get('/whatsapp/templates/all', {
          params: {
            provider: 'twilio',
            page: 1,
            page_size: 100,
            ...(initialWabaConfigId ? { waba_id: initialWabaConfigId } : {}),
          },
        }).catch(() => ({ data: { items: [] } })),
        api.get('/whatsapp/status').catch(() => ({ data: { mode: 'gupshup' } })),
        detailReq,
        perLineReq,
        api.get('/whatsapp/gupshup/partner/status').catch(() => ({ data: { connected: false } })),
        api.get('/whatsapp/gupshup/partner/reassign-specs').catch(() => ({ data: { templates: [] } })),
      ]);
      setWaMode(String(status.data?.mode || 'gupshup').toLowerCase());
      setWabaDetail(detail.data || null);
      const detectedProvider = String(detail.data?.provider || '').toLowerCase();
      const lineProvider = detectedProvider === 'twilio' ? 'twilio' : 'gupshup';
      setWabaProvider(lineProvider);
      setPartnerReady(Boolean(partnerRes?.data?.connected));
      setReassignSpecs(Array.isArray(specsRes?.data?.templates) ? specsRes.data.templates : []);

      const mergedEvents = { ...(cfg.data?.events || {}) };
      const perLine = perLineRes?.data || null;
      if (perLine && initialWabaConfigId && wabaAccountId) {
        Object.entries(PER_LINE_EVENT_KEYS).forEach(([uiKey, subKey]) => {
          const sub = perLine[subKey] || {};
          mergedEvents[uiKey] = {
            ...defaultEventConfig,
            ...sub,
            provider: lineProvider,
            waba_config_id: initialWabaConfigId,
          };
        });
      }
      setEvents(mergedEvents);
      const list = Array.isArray(nums.data?.numbers) ? nums.data.numbers : [];
      setNumbers(list);
      const twilioSaved = Array.isArray(twilioTpls.data?.items || twilioTpls.data?.templates)
        ? (twilioTpls.data?.items || twilioTpls.data?.templates)
        : [];
      setTwilioTemplates(
        twilioSaved
          .filter((t) => (String(t?.provider || 'twilio').toLowerCase() === 'twilio'))
          .map((t) => ({ ...t, content_sid: t.content_sid || t.template_id || t.id }))
          .filter((t) => String(t.content_sid || '').trim())
      );

      const tplMap = {};
      await Promise.all(list.map(async (n) => {
        try {
          const r = await api.get(`/whatsapp/gupshup/saved-templates/${n.id}`);
          tplMap[n.id] = Array.isArray(r.data?.templates) ? r.data.templates : [];
        } catch {
          tplMap[n.id] = [];
        }
      }));
      setTemplatesByWaba(tplMap);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar los avisos de WhatsApp');
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openEvent = (eventKey) => {
    if (initialWabaConfigId) {
      setEvents((prev) => {
        const cur = { ...defaultEventConfig, ...(prev[eventKey] || {}) };
        const targetProvider = wabaProvider === 'twilio' ? 'twilio' : 'gupshup';
        const sameProvider = (cur.provider || 'gupshup') === targetProvider;
        if (targetProvider === 'twilio') {
          return {
            ...prev,
            [eventKey]: {
              ...cur,
              provider: 'twilio',
              waba_id: initialWabaConfigId,
              waba_config_id: '',
              template_id: '',
              twilio_content_sid: sameProvider ? cur.twilio_content_sid : '',
              twilio_content_variables: sameProvider ? cur.twilio_content_variables : {},
              variable_mapping: sameProvider ? cur.variable_mapping : [],
              message_text: sameProvider ? cur.message_text : '',
            },
          };
        }
        return {
          ...prev,
          [eventKey]: {
            ...cur,
            provider: 'gupshup',
            waba_config_id: initialWabaConfigId,
            twilio_content_sid: '',
            twilio_content_variables: {},
            template_id: sameProvider ? cur.template_id : '',
            variable_mapping: sameProvider ? cur.variable_mapping : [],
            message_text: sameProvider ? cur.message_text : '',
          },
        };
      });
    }
    setActiveEventKey(eventKey);
    setDialogOpen(true);
  };

  const activeEvent = useMemo(() => ({ ...defaultEventConfig, ...(events[activeEventKey] || {}) }), [events, activeEventKey]);
  const activeProvider = activeEvent.provider || 'gupshup';
  const allGupshupTemplates = templatesByWaba[activeEvent.waba_config_id] || [];
  const activeGupshupTemplates = allGupshupTemplates;
  const selectedGupshupTemplate = activeGupshupTemplates.find((t) => t.id === activeEvent.template_id) || null;

  const selectedTwilioTemplate = twilioTemplates.find((t) => (
    t.content_sid === activeEvent.twilio_content_sid || t.template_id === activeEvent.twilio_content_sid
  )) || null;
  const selectedTemplate = activeProvider === 'twilio' ? selectedTwilioTemplate : selectedGupshupTemplate;
  const selectedStatus = statusPlain(selectedTemplate?.status);

  const indexes = useMemo(() => {
    if (activeProvider === 'twilio') {
      const fromTpl = (selectedTwilioTemplate?.variables || []).map((v) => Number(v)).filter((n) => Number.isFinite(n));
      if (fromTpl.length) return [...new Set(fromTpl)].sort((a, b) => a - b);
      return extractIndexes(selectedTwilioTemplate?.body || '');
    }
    return extractIndexes(selectedGupshupTemplate?.body || '');
  }, [activeProvider, selectedTwilioTemplate, selectedGupshupTemplate]);

  const updateActiveEvent = (patch) => {
    setEvents((prev) => {
      const prevEvent = { ...defaultEventConfig, ...(prev[activeEventKey] || {}) };
      const nextEvent = { ...prevEvent, ...patch };
      if (nextEvent.provider === 'twilio') {
        nextEvent.twilio_content_variables = buildTwilioVariableSchema(nextEvent.variable_mapping || []);
      }
      return { ...prev, [activeEventKey]: nextEvent };
    });
  };

  useEffect(() => {
    if (!activeEventKey || !selectedTemplate || indexes.length === 0) return;
    const byIdx = new Map((activeEvent.variable_mapping || []).map((m) => [Number(m.index), m]));
    const mapping = indexes.map((idx) => ({
      index: idx,
      source: byIdx.get(idx)?.source || defaultSource(idx),
      custom_value: byIdx.get(idx)?.custom_value || '',
    }));
    updateActiveEvent({ variable_mapping: mapping });
  }, [activeEventKey, selectedTemplate?.id, selectedTemplate?.content_sid, indexes.join(',')]);

  const preview = useMemo(() => {
    const body = String(selectedTemplate?.body || '');
    return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
      const row = (activeEvent.variable_mapping || []).find((m) => Number(m.index) === Number(n));
      if (!row) return `{{${n}}}`;
      if (row.source === 'custom') return row.custom_value || 'Texto fijo';
      return sourceOptions.find((s) => s.value === row.source)?.example || `{{${n}}}`;
    });
  }, [selectedTemplate, activeEvent.variable_mapping]);

  const save = async () => {
    try {
      if (selectedStatus.tone === 'wait' && activeEvent.enabled) {
        toast.error('Ese mensaje todavía lo está revisando WhatsApp. Cuando esté listo, vuelve y enciéndelo.');
        return;
      }
      if (selectedStatus.tone === 'bad' && activeEvent.enabled) {
        toast.error('WhatsApp rechazó ese mensaje. Pide uno nuevo o elige otro que esté listo.');
        return;
      }
      let workingEvents = events;
      if (activeEventKey) {
        const cur = { ...defaultEventConfig, ...(events[activeEventKey] || {}) };
        const hasTemplate = Boolean(cur.template_id || cur.twilio_content_sid || cur.message_text);
        if (hasTemplate && !cur.enabled && selectedStatus.tone === 'ok') {
          workingEvents = { ...events, [activeEventKey]: { ...cur, enabled: true } };
          setEvents(workingEvents);
        }
      }

      if (activeEventKey && PER_LINE_EVENT_KEYS[activeEventKey] && wabaAccountId) {
        const subKey = PER_LINE_EVENT_KEYS[activeEventKey];
        const cur = { ...defaultEventConfig, ...(workingEvents[activeEventKey] || {}) };
        await api.put(`/waba-accounts/${wabaAccountId}/notifications`, {
          [subKey]: {
            enabled: Boolean(cur.enabled),
            template_id: cur.template_id || null,
            variable_mapping: cur.variable_mapping || [],
            twilio_content_sid: cur.twilio_content_sid || null,
            twilio_content_variables: cur.twilio_content_variables || {},
            message_text: cur.message_text || null,
          },
        });
        toast.success('Listo. Este aviso quedó guardado.');
        setDialogOpen(false);
        return;
      }

      const payload = { ...workingEvents };
      if (wabaAccountId) {
        Object.keys(PER_LINE_EVENT_KEYS).forEach((k) => { delete payload[k]; });
      }
      const r = await api.post('/whatsapp/notification-config', { events: payload });
      if (r?.data?.events) {
        const serverEvents = { ...r.data.events };
        if (wabaAccountId) {
          Object.keys(PER_LINE_EVENT_KEYS).forEach((k) => { delete serverEvents[k]; });
        }
        setEvents((prev) => ({ ...prev, ...serverEvents }));
      }
      toast.success('Listo. Este aviso quedó guardado.');
      setDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    }
  };

  const submitReassignTemplates = async () => {
    const configId = initialWabaConfigId || numbers[0]?.id || '';
    if (!configId && !wabaAccountId) {
      toast.error('Primero elige el número de WhatsApp de esta empresa.');
      return;
    }
    setSubmittingReassign(true);
    setSubmitNote('');
    try {
      const r = await api.post('/whatsapp/gupshup/partner/templates', {
        waba_config_id: configId || undefined,
        waba_account_id: wabaAccountId || undefined,
      });
      const pending = (r?.data?.results || []).filter((x) => x.status === 'pending').length;
      const exists = (r?.data?.results || []).filter((x) => x.status === 'already_exists').length;
      const failed = (r?.data?.results || []).filter((x) => !['pending', 'already_exists'].includes(x.status)).length;
      let note = 'Listo. WhatsApp está leyendo los 2 mensajes. Tarda de unas horas a 2 días. Cuando estén aprobados, entra a Configurar y enciéndelos.';
      if (exists && !pending) {
        note = 'Esos 2 mensajes ya se habían pedido. WhatsApp sigue revisándolos. No hay que volver a enviarlos.';
      } else if (pending && exists) {
        note = `Se pidió aprobación de ${pending} mensaje(s). ${exists} ya estaba en revisión.`;
      }
      if (failed) {
        note = 'WhatsApp no aceptó uno de los textos. Revisa el mensaje de error o pide ayuda a quien administra Dealider.';
      }
      setSubmitNote(note);
      toast.success(note);
      await loadAll();
    } catch (e) {
      const raw = String(e.response?.data?.detail || '');
      let friendly = 'No se pudieron enviar los mensajes a WhatsApp.';
      if (raw.includes('GUPSHUP_PARTNER') || raw.includes('Faltan')) {
        friendly = 'Falta conectar Gupshup en el servidor. Eso lo hace quien administra Dealider, no se hace desde esta pantalla.';
      } else if (raw.includes('Login de partner')) {
        friendly = 'Gupshup no aceptó la cuenta del servidor. Quien administra Dealider debe revisar el acceso.';
      } else if (raw.includes('app_id') || raw.includes('token de la app')) {
        friendly = 'Esta línea no está ligada a tu cuenta de Gupshup Partner. Las plantillas solo se pueden enviar a revisión desde líneas que estén en esa cuenta. Pídele a Gupshup que la migren, o crea las 2 plantillas a mano desde el panel de Gupshup.';
      } else if (raw) {
        friendly = raw;
      }
      toast.error(friendly);
      setSubmitNote(friendly);
    } finally {
      setSubmittingReassign(false);
    }
  };

  const Row = ({ item }) => {
    const cfg = { ...defaultEventConfig, ...(events[item.key] || {}) };
    const provider = cfg.provider || 'gupshup';
    const waba = numbers.find((n) => n.id === cfg.waba_config_id);
    const gupshupTpl = (templatesByWaba[cfg.waba_config_id] || []).find((t) => t.id === cfg.template_id);
    const twilioTpl = twilioTemplates.find((t) => (
      t.content_sid === cfg.twilio_content_sid || t.template_id === cfg.twilio_content_sid
    ));
    const st = statusPlain(gupshupTpl?.status);
    const help = EVENT_HELP[item.key];

    let summary = 'Todavía no está configurado';
    if (cfg.enabled && provider === 'twilio') {
      summary = `${twilioTpl?.name || twilioTpl?.friendly_name || 'Mensaje Twilio'} · ${(cfg.variable_mapping || []).length} datos del lead`;
    } else if (cfg.enabled && gupshupTpl) {
      summary = `${st.label || 'Mensaje'} · ${waba?.app_name || 'WhatsApp'} · ${(cfg.variable_mapping || []).length} datos`;
    } else if (cfg.enabled) {
      summary = 'Encendido, pero falta elegir el mensaje';
    }

    const boundElsewhere = !PER_LINE_EVENT_KEYS[item.key]
      && provider === 'gupshup' && cfg.waba_config_id && initialWabaConfigId && cfg.waba_config_id !== initialWabaConfigId;
    const boundWaba = boundElsewhere ? numbers.find((n) => n.id === cfg.waba_config_id) : null;

    return (
      <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-black/20" data-testid={`whatsapp-notif-row-${item.key}`}>
        <div className="min-w-0 pr-3">
          <p className="text-sm text-white flex items-center gap-1.5" data-testid={`whatsapp-notif-row-title-${item.key}`}>
            <span>{item.title}</span>
            {help ? <BigTooltip title={help.title} body={help.body} useCase={help.useCase} /> : null}
          </p>
          <p className={`text-[11px] ${cfg.enabled ? (toneClass[st.tone] || 'text-zinc-400') : 'text-zinc-400'}`} data-testid={`whatsapp-notif-row-summary-${item.key}`}>
            {summary}
          </p>
          {boundElsewhere ? (
            <p className="text-[11px] text-amber-300" data-testid={`whatsapp-notif-row-bound-elsewhere-${item.key}`}>
              {`Se envía por: ${boundWaba?.app_name || 'otro WhatsApp'}${boundWaba?.source_phone ? ` — ${boundWaba.source_phone}` : ''}`}
            </p>
          ) : null}
        </div>
        <Button variant="outline" size="sm" onClick={() => openEvent(item.key)} data-testid={`whatsapp-notif-row-configure-${item.key}`}>
          Configurar
        </Button>
      </div>
    );
  };

  const visibleLeadEvents = leadEvents;
  const visibleVendorEvents = vendorEvents;
  const activeHelp = EVENT_HELP[activeEventKey];

  return (
    <div className="space-y-4" data-testid="whatsapp-notificaciones-root">
      <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-notificaciones-howto">
        <CardContent className="p-4 space-y-2">
          <p className="text-sm text-white flex items-center gap-1.5">
            Cómo funcionan estos avisos
            <BigTooltip
              title="No se inventa el texto al momento"
              body="WhatsApp obliga a usar un mensaje que ellos ya leyeron. Aquí solo eliges CUÁNDO se manda y QUÉ huecos se llenan (nombre, teléfono…)."
              useCase="Ana configura “Nuevo lead asignado”, elige el mensaje aprobado y enciende el aviso. Cuando entre María, Ana recibe el WhatsApp sola."
            />
          </p>
          <ol className="text-[12px] text-zinc-300 space-y-1 list-decimal list-inside">
            <li>Si el mensaje todavía no existe, pídele a WhatsApp que lo apruebe (abajo, en reasignación).</li>
            <li>Cuando diga “Lista para usar”, entra a Configurar, elige el mensaje y enciéndelo.</li>
            <li>Si está apagado, no se manda nada. Si está en revisión, WhatsApp lo va a rechazar si lo mandas.</li>
          </ol>
        </CardContent>
      </Card>

      {(initialWabaConfigId
        ? (!wabaDetail || wabaDetail.status !== 'active')
        : !numbers.length) ? (
        <Card className="bg-zinc-950/70 border-amber-500/20" data-testid="whatsapp-notificaciones-warning-no-waba">
          <CardContent className="p-4 text-amber-300 text-sm">
            Falta un WhatsApp activo en esta empresa. Configúralo en la pestaña Números antes de encender avisos.
          </CardContent>
        </Card>
      ) : null}

      <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-notificaciones-lead-block">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-1.5">
            Avisos al lead (el cliente)
            <BigTooltip
              title="Le llegan a María"
              body="Estos WhatsApp salen al teléfono del lead, no al del vendedor."
              useCase="María acaba de registrarse y recibe el saludo. Ana no ve este mensaje."
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">{visibleLeadEvents.map((i) => <Row key={i.key} item={i} />)}</CardContent>
      </Card>

      <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-notificaciones-vendor-block">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-1.5">
            Avisos al vendedor
            <BigTooltip
              title="Le llegan a Ana o a Carlos"
              body="Salen al teléfono del asesor. El cliente no los ve."
              useCase="Luis pasa a María de Ana a Carlos. Ana y Carlos reciben cada uno su aviso."
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">{visibleVendorEvents.map((i) => <Row key={i.key} item={i} />)}</CardContent>
      </Card>

      <Card className="bg-zinc-950/70 border-emerald-500/15" data-testid="whatsapp-notificaciones-reassign-submit">
        <CardContent className="p-4 space-y-3">
          <p className="text-sm text-white flex items-center gap-1.5">
            Pedir aprobación de los avisos de reasignación
            <BigTooltip
              title="WhatsApp tiene que leer el texto"
              body="No podemos “aprobar” nosotros. Solo enviamos los 2 mensajes a revisión. Tarda de unas horas a 2 días. Una vez aprobados, los enciendes arriba."
              useCase="Das clic una vez. Luego esperas. Cuando digan “Lista para usar”, entras a las 2 casillas de arriba y las enciendes."
            />
          </p>
          <p className="text-[12px] text-zinc-400">
            Si le quitas un lead a Ana y se lo das a Carlos, los dos deben enterarse.
            Estos son los 2 textos que WhatsApp tiene que aceptar. Tú no escribes código: solo pides la revisión.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(reassignSpecs.length ? reassignSpecs : [
              { plain_title: 'Al que pierde el lead', example: 'Luis Pérez reasignó el lead María López…', plain_for: 'Ana' },
              { plain_title: 'Al que recibe el lead', example: 'Se te asignó un lead: María López…', plain_for: 'Carlos' },
            ]).map((spec) => (
              <div key={spec.event_key || spec.plain_title} className="rounded-xl p-3 bg-[#ece5dd] border border-yellow-900/20">
                <p className="text-[10px] uppercase tracking-wide text-zinc-600 mb-1">
                  {spec.plain_title}{spec.plain_for ? ` · ${spec.plain_for}` : ''}
                </p>
                <p className="text-[12px] text-zinc-900 whitespace-pre-wrap">{spec.example}</p>
              </div>
            ))}
          </div>

          {partnerReady === false ? (
            <p className="text-[12px] text-amber-300" data-testid="whatsapp-notif-partner-missing">
              Falta conectar Gupshup en el servidor. Eso lo hace quien administra Dealider. Esta pantalla no pide contraseñas.
            </p>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            disabled={submittingReassign || partnerReady === false}
            onClick={submitReassignTemplates}
            data-testid="whatsapp-notif-submit-reassign-templates"
          >
            {submittingReassign ? 'Enviando a WhatsApp…' : 'Pedirle a WhatsApp que apruebe estos 2 mensajes'}
          </Button>
          {submitNote ? (
            <p className="text-[12px] text-zinc-300" data-testid="whatsapp-notif-submit-note">{submitNote}</p>
          ) : (
            <p className="text-[11px] text-zinc-500">
              Un clic. Sin contraseñas. WhatsApp responde después; Dealider no puede saltarse esa revisión.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="whatsapp-notif-config-dialog">
          <DialogHeader>
            <DialogTitle data-testid="whatsapp-notif-config-dialog-title" className="flex items-center gap-1.5">
              {activeHelp?.title || 'Configurar aviso'}
              {activeHelp ? <BigTooltip title={activeHelp.title} body={activeHelp.body} useCase={activeHelp.useCase} /> : null}
            </DialogTitle>
            <DialogDescription data-testid="whatsapp-notif-config-dialog-description">
              Elige el mensaje aprobado, di qué dato va en cada hueco y enciende el aviso.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/10">
              <p className="text-sm text-white flex items-center gap-1.5">
                Encender este aviso
                <FieldTooltip tip="Si está apagado, no se manda nada aunque ya hayas elegido un mensaje." />
              </p>
              <Switch checked={Boolean(activeEvent.enabled)} onCheckedChange={(v) => updateActiveEvent({ enabled: v })} data-testid="whatsapp-notif-config-enable-toggle" />
            </div>

            <div className="rounded-lg border border-white/10 p-3 bg-black/20" data-testid="whatsapp-notif-config-provider-box" hidden={Boolean(initialWabaConfigId)}>
              <Label className="text-xs flex items-center gap-1.5">
                Por dónde se manda
                <FieldTooltip tip="Gupshup es el WhatsApp oficial de Dealider. Twilio es el otro proveedor. Evolution es el WhatsApp con QR, texto libre." />
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {waMode === 'qr' && (
                  <button
                    type="button"
                    className={`px-3 py-1.5 rounded-md text-xs border ${activeProvider === 'qr' ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-white/10 text-zinc-300'}`}
                    onClick={() => updateActiveEvent({ provider: 'qr', waba_config_id: '', template_id: '', twilio_content_sid: '', twilio_content_variables: {}, variable_mapping: [] })}
                    data-testid="whatsapp-notif-config-provider-qr"
                  >
                    WhatsApp con QR (texto libre)
                  </button>
                )}
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md text-xs border ${activeProvider === 'gupshup' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-white/10 text-zinc-300'}`}
                  onClick={() => updateActiveEvent({ provider: 'gupshup', twilio_content_sid: '', twilio_content_variables: {} })}
                  data-testid="whatsapp-notif-config-provider-gupshup"
                >
                  WhatsApp oficial (Gupshup)
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md text-xs border ${activeProvider === 'twilio' ? 'border-blue-500/40 bg-blue-500/10 text-blue-300' : 'border-white/10 text-zinc-300'}`}
                  onClick={() => updateActiveEvent({ provider: 'twilio', waba_config_id: '', template_id: '' })}
                  data-testid="whatsapp-notif-config-provider-twilio"
                >
                  Twilio
                </button>
              </div>
            </div>

            {activeProvider === 'qr' ? (
              <div data-testid="whatsapp-notif-config-qr-text-box">
                <Label className="text-xs flex items-center gap-1.5">
                  Texto del mensaje
                  <FieldTooltip tip="Como es WhatsApp con QR, puedes escribir libre. Toca las pastillas para meter el nombre o el teléfono de María." />
                </Label>
                <textarea
                  ref={textareaRef}
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-xs min-h-[100px]"
                  value={activeEvent.message_text || ''}
                  onChange={(e) => updateActiveEvent({ message_text: e.target.value })}
                  placeholder="Hola {lead_name}, gracias por tu interés. Tu vendedor {lead_vendor} se pondrá en contacto."
                  data-testid="whatsapp-notif-config-qr-text"
                />
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid="whatsapp-notif-config-qr-vars">
                  {QR_VARS.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertQrVariable(v.key)}
                      className="px-2 py-0.5 rounded-full text-[10px] border border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer text-zinc-300 transition-colors"
                      data-testid={`whatsapp-notif-config-qr-var-${v.key.replace(/[{}]/g, '')}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : activeProvider === 'gupshup' ? (
              <>
                <div>
                  <Label className="text-xs flex items-center gap-1.5">
                    Desde qué WhatsApp se envía
                    <FieldTooltip tip="El número oficial de esta empresa (por ejemplo GMCCUAUTLA). El cliente ve ese número." />
                  </Label>
                  {initialWabaConfigId ? (
                    <div
                      className="w-full mt-1 bg-gray-800 border border-white/10 rounded-md px-2 py-2 text-xs text-zinc-200"
                      data-testid="whatsapp-notif-config-waba-locked"
                    >
                      {(() => {
                        const w = numbers.find((n) => n.id === initialWabaConfigId);
                        if (w) return `${w.app_name} — ${w.source_phone}`;
                        return initialWabaConfigId;
                      })()}
                    </div>
                  ) : (
                    <select
                      className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-xs"
                      value={activeEvent.waba_config_id || ''}
                      onChange={(e) => updateActiveEvent({ waba_config_id: e.target.value, template_id: '', variable_mapping: [] })}
                      data-testid="whatsapp-notif-config-waba-select"
                    >
                      <option value="">Elige el número</option>
                      {numbers.map((n) => <option key={n.id} value={n.id}>{`${n.app_name} — ${n.source_phone}`}</option>)}
                    </select>
                  )}
                </div>

                <div>
                  <Label className="text-xs flex items-center gap-1.5">
                    Qué mensaje se manda
                    <FieldTooltip tip="Solo usa los que dicen “Lista para usar”. Si dice “la está revisando”, todavía no sirve." />
                  </Label>
                  <select
                    className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-xs"
                    value={activeEvent.template_id || ''}
                    onChange={(e) => updateActiveEvent({ template_id: e.target.value })}
                    data-testid="whatsapp-notif-config-template-select"
                  >
                    <option value="">Elige un mensaje</option>
                    {activeGupshupTemplates.map((t) => {
                      const st = statusPlain(t.status);
                      const vars = extractIndexes(t.body || '').length;
                      return (
                        <option key={t.id} value={t.id}>
                          {`${t.name || 'Mensaje'} · ${st.label || t.status || 'sin estado'}${vars ? ` · ${vars} datos` : ''}`}
                        </option>
                      );
                    })}
                  </select>
                  {activeGupshupTemplates.length === 0 ? (
                    <p className="mt-1.5 text-[11px] text-amber-300" data-testid="whatsapp-notif-config-template-empty-hint">
                      Aún no hay mensajes en este WhatsApp. Pide la aprobación abajo (reasignación) o sincroniza plantillas.
                    </p>
                  ) : selectedStatus.label ? (
                    <p className={`mt-1.5 text-[11px] ${toneClass[selectedStatus.tone]}`} data-testid="whatsapp-notif-config-template-status">
                      {selectedStatus.label}
                      {selectedStatus.tone === 'wait' ? ' — no lo enciendas todavía.' : ''}
                      {selectedStatus.tone === 'bad' ? ' — elige otro o pide uno nuevo.' : ''}
                    </p>
                  ) : null}
                </div>
              </>
            ) : (
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Mensaje de Twilio
                  <FieldTooltip tip="Twilio llama a estos mensajes “Content SID”. Elige el que ya configuraste en Plantillas." />
                </Label>
                <select
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-xs"
                  value={activeEvent.twilio_content_sid || ''}
                  onChange={(e) => updateActiveEvent({ twilio_content_sid: e.target.value, variable_mapping: [] })}
                  data-testid="whatsapp-notif-config-twilio-content-sid-select"
                >
                  <option value="">Elige un mensaje Twilio</option>
                  {twilioTemplates.map((t) => (
                    <option key={t.content_sid} value={t.content_sid}>{`${t.name || t.friendly_name || 'Mensaje Twilio'}`}</option>
                  ))}
                </select>
                {twilioTemplates.length === 0 ? (
                  <div className="mt-2 rounded-md border border-amber-500/20 bg-amber-500/5 p-2" data-testid="whatsapp-notif-config-twilio-empty-state">
                    <p className="text-xs text-amber-200">Todavía no hay mensajes de Twilio guardados.</p>
                    <p className="text-[11px] text-amber-300/80">Ve a la pestaña Plantillas y sincroniza Twilio.</p>
                  </div>
                ) : null}
              </div>
            )}

            {activeProvider !== 'qr' && indexes.length > 0 ? (
              <div className="space-y-2" data-testid="whatsapp-notif-config-mapping-box">
                <p className="text-xs text-zinc-300 flex items-center gap-1.5">
                  Qué dato va en cada hueco
                  <FieldTooltip tip="El mensaje tiene espacios como {{1}}. Aquí dices si {{1}} es el nombre de María, su teléfono, o un texto fijo." />
                </p>
                {indexes.map((idx) => {
                  const row = (activeEvent.variable_mapping || []).find((m) => Number(m.index) === idx) || { index: idx, source: defaultSource(idx), custom_value: '' };
                  return (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center" data-testid={`whatsapp-notif-config-mapping-row-${idx}`}>
                      <p className="text-xs text-emerald-300">{`Hueco ${idx}`}</p>
                      <select
                        className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs"
                        value={row.source}
                        onChange={(e) => {
                          const next = [...(activeEvent.variable_mapping || [])];
                          const i = next.findIndex((x) => Number(x.index) === idx);
                          const upd = { ...row, source: e.target.value, custom_value: e.target.value === 'custom' ? row.custom_value || '' : '' };
                          if (i >= 0) next[i] = upd; else next.push(upd);
                          next.sort((a, b) => Number(a.index) - Number(b.index));
                          updateActiveEvent({ variable_mapping: next });
                        }}
                        data-testid={`whatsapp-notif-config-mapping-source-${idx}`}
                      >
                        {sourceOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>

                      {row.source === 'custom' ? (
                        <Input
                          value={row.custom_value || ''}
                          onChange={(e) => {
                            const next = [...(activeEvent.variable_mapping || [])];
                            const i = next.findIndex((x) => Number(x.index) === idx);
                            const upd = { ...row, custom_value: e.target.value };
                            if (i >= 0) next[i] = upd; else next.push(upd);
                            updateActiveEvent({ variable_mapping: next });
                          }}
                          placeholder="Texto que no cambia"
                          data-testid={`whatsapp-notif-config-mapping-custom-${idx}`}
                        />
                      ) : (
                        <p className="text-[11px] text-zinc-400" data-testid={`whatsapp-notif-config-mapping-example-${idx}`}>
                          Ej: {sourceOptions.find((s) => s.value === row.source)?.example}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : activeProvider !== 'qr' ? (
              <p className="text-xs text-zinc-500" data-testid="whatsapp-notif-config-mapping-empty">
                Este mensaje no tiene huecos. Se manda tal cual.
              </p>
            ) : null}

            <div className="rounded-xl p-3 bg-[#ece5dd] border border-yellow-900/30" data-testid="whatsapp-notif-config-preview-bubble">
              <p className="text-[10px] uppercase tracking-wide text-zinc-600 mb-1">Así se ve en el celular</p>
              <p className="text-[12px] text-zinc-900 whitespace-pre-wrap">
                {activeProvider === 'qr' ? (activeEvent.message_text || 'Escribe el texto para verlo aquí') : (preview || 'Elige un mensaje para verlo aquí')}
              </p>
            </div>

            <div className="flex items-center justify-end gap-3" data-testid="whatsapp-notif-config-actions">
              <button type="button" className="text-xs text-zinc-400 hover:text-white" onClick={() => setDialogOpen(false)} data-testid="whatsapp-notif-config-discard-button">
                Salir sin guardar
              </button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-500" data-testid="whatsapp-notif-config-save-button">
                <BellRing className="w-3.5 h-3.5 mr-1" />Guardar aviso
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
